import React, { useEffect, useState } from 'react';
import PresignedAudio from '../common/PresignedAudio';
import FileTypeIcon from '../common/FileTypeIcon';
import {
  formatAudioDuration,
  formatFileSize,
  getFileExtensionLabel,
  getFileIconKind,
  getLinkDisplayTitle,
  isLinkAttachment,
  isRecordedVoiceNote,
  linkDomainFromUrl,
  parseVoiceNoteDurationSeconds,
  splitFileNameParts,
} from '../../utils/fileUtils';
import { LinkIcon } from './TimelineLinkCard';

const AUDIO_EXT_RE = /\.(mp3|wav|ogg|m4a|aac|flac|webm|opus)$/i;

export const isAudioAttachment = (attachment) => {
  if (!attachment) return false;
  if (String(attachment.fileType || '').toUpperCase() === 'AUDIO') return true;
  const name = attachment.fileName || attachment.originalFileName || '';
  return AUDIO_EXT_RE.test(name);
};

const VoiceNoteIcon = ({ className = 'w-10 h-10' }) => (
  <span
    className={`inline-flex items-center justify-center rounded-xl bg-purple-600 text-white shadow-sm shadow-purple-500/30 ${className}`}
    aria-hidden="true"
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14a3 3 0 003-3V7a3 3 0 10-6 0v4a3 3 0 003 3zm6-3a6 6 0 01-12 0m6 6v3m-4 0h8" />
    </svg>
  </span>
);

const DownloadIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const AudioFileCard = ({
  attachment,
  fileName,
  baseName,
  extension,
  onClick,
  onRemove,
  className = '',
}) => {
  const storedDuration = Number(attachment.durationSeconds);
  const namedDuration = parseVoiceNoteDurationSeconds(fileName);
  const initialDuration = (
    Number.isFinite(storedDuration) && storedDuration > 0
      ? storedDuration
      : namedDuration
  );
  const [durationSeconds, setDurationSeconds] = useState(initialDuration);

  useEffect(() => {
    const next = Number(attachment.durationSeconds);
    const fromName = parseVoiceNoteDurationSeconds(fileName);
    const resolved = Number.isFinite(next) && next > 0 ? next : fromName;
    if (Number.isFinite(resolved) && resolved > 0) {
      setDurationSeconds(resolved);
    }
  }, [attachment.durationSeconds, fileName]);
  const durationLabel = formatAudioDuration(durationSeconds);
  const sizeLabel = attachment.fileSize ? formatFileSize(attachment.fileSize) : '';
  const isVoiceNote = isRecordedVoiceNote(fileName);
  const metaLabel = isVoiceNote
    ? sizeLabel
    : [durationLabel, sizeLabel].filter(Boolean).join(' · ');

  return (
    <div
      className={`relative flex flex-col gap-2 w-full max-w-sm px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/90 ${className}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        {isVoiceNote ? (
          <VoiceNoteIcon className="w-10 h-10 flex-shrink-0" />
        ) : (
          <FileTypeIcon kind="audio" attachment={attachment} size="md" className="flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          {isVoiceNote ? (
            metaLabel ? (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {metaLabel}
              </p>
            ) : null
          ) : (
            <>
              <div className="flex items-center gap-2 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate min-w-0" title={fileName}>
                  <span className="truncate">{baseName || fileName}</span>
                  {extension && (
                    <span className="font-normal text-gray-500 dark:text-gray-400">{extension}</span>
                  )}
                </p>
              </div>
              {metaLabel ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {metaLabel}
                </p>
              ) : null}
            </>
          )}
        </div>
        {onClick && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClick(attachment);
            }}
            className="flex-shrink-0 p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
            title="Download"
            aria-label={isVoiceNote ? 'Download voice note' : `Download ${fileName}`}
          >
            <DownloadIcon />
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="flex-shrink-0 p-2 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 transition-colors"
            title="Remove"
            aria-label={isVoiceNote ? 'Remove voice note' : `Remove ${fileName}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      <PresignedAudio
        fileKey={attachment.fileUrl}
        className="w-full"
        durationSeconds={durationSeconds}
        onDuration={(value) => {
          if (!Number.isFinite(value) || value < 0.5) return;
          setDurationSeconds((prev) => {
            if (prev && prev >= 1 && value < prev * 0.5) return prev;
            return value;
          });
        }}
      />
    </div>
  );
};

const TimelineFileCard = ({
  attachment,
  onClick,
  onRemove,
  className = '',
}) => {
  if (!attachment) return null;

  const isLink = isLinkAttachment(attachment);

  if (isLink) {
    const link = {
      url: attachment.fileUrl,
      title: attachment.fileName,
      customTitle: attachment.linkCustomTitle,
      description: attachment.linkDescription,
      imageUrl: attachment.linkImageUrl,
      siteName: attachment.linkSiteName,
      domain: attachment.linkDomain,
    };
    const title = getLinkDisplayTitle(link);
    const domain = link.domain || link.siteName || linkDomainFromUrl(link.url);

    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onClick) {
            onClick(attachment);
            return;
          }
          if (link.url) {
            window.open(link.url.replace(/&amp;/g, '&'), '_blank', 'noopener,noreferrer');
          }
        }}
        title={title}
        className={`relative flex items-center gap-3 w-full max-w-sm px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/90 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors text-left ${className}`}
      >
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center overflow-hidden">
          {link.imageUrl ? (
            <img src={link.imageUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <LinkIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate min-w-0">
              {title}
            </p>
            <span className="flex-shrink-0 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
              Link
            </span>
          </div>
          {domain && (
            <p className="text-xs text-primary-600 dark:text-primary-400 mt-0.5 truncate">
              {domain}
            </p>
          )}
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="absolute top-1.5 right-1.5 p-0.5 rounded-full bg-black/50 text-white hover:bg-red-600 transition-colors"
            title="Remove"
            aria-label={`Remove ${title}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </button>
    );
  }

  const fileName = attachment.fileName || attachment.originalFileName || 'File';
  const { baseName, extension } = splitFileNameParts(fileName);
  const ext = extension ? getFileExtensionLabel(fileName).toLowerCase() : '';
  const isAudio = isAudioAttachment(attachment);
  const iconKind =
    isAudio
      ? 'audio'
      : attachment.fileType === 'VIDEO'
        ? 'video'
        : getFileIconKind(ext);

  if (isAudio) {
    return (
      <AudioFileCard
        attachment={attachment}
        fileName={fileName}
        baseName={baseName}
        extension={extension}
        onClick={onClick}
        onRemove={onRemove}
        className={className}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.(attachment);
      }}
      title={fileName}
      className={`relative flex items-center gap-3 w-full max-w-sm px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/90 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors text-left ${className}`}
    >
      <FileTypeIcon
        kind={iconKind}
        attachment={attachment}
        extension={ext}
        size="md"
        className="flex-shrink-0"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate min-w-0">
            <span className="truncate">{baseName || fileName}</span>
            {extension && (
              <span className="font-normal text-gray-500 dark:text-gray-400">{extension}</span>
            )}
          </p>
        </div>
        {attachment.fileSize ? (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {formatFileSize(attachment.fileSize)}
          </p>
        ) : null}
      </div>

      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="absolute top-1.5 right-1.5 p-0.5 rounded-full bg-black/50 text-white hover:bg-red-600 transition-colors"
          title="Remove"
          aria-label={`Remove ${fileName}`}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </button>
  );
};

export default TimelineFileCard;
